import { useCallback, useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { clsx } from 'clsx'
import { addHours, addMinutes, isAfter, isPast } from 'date-fns'

import type { CharCountService } from '@buffer-mono/char-toolkit'
import { SplitProtected, useSplitEnabled } from '@buffer-mono/features'
import {
  ArrowDownIcon,
  ArrowDownUpIcon,
  ArrowUpIcon,
  Button,
  CheckIcon,
  XIcon,
  MessageSquareQuoteIcon,
  CopyPlusIcon,
  DropdownMenu,
  ExpandIcon,
  ExternalLinkIcon,
  Flex,
  IconButton,
  LinkIcon,
  FileOutputIcon,
  formatTimeZone,
  PencilIcon,
  Popover,
  QueueBottomIcon,
  SendClockIcon,
  SendHorizontalIcon,
  ForwardIcon,
  Text,
  toast,
  Tooltip,
  UserRoundCheckIcon,
} from '@buffer-mono/popcorn'
import type { PopoverProps } from '@buffer-mono/popcorn'

import {
  CopyIdMenuItem,
  OpenInHQMenuItem,
  useShouldShowDeveloperData,
} from '~publish/components/DeveloperData'
import {
  PostGroupDialog,
  POST_GROUPS_UI_FLAG,
} from '~publish/components/PostDialogs/PostGroupDialog'
import { normalizeNetworkName } from '~publish/helpers/channels'
import { isOfType } from '~publish/helpers/typeGuards'
import { Services } from '~publish/legacy/constants/services'
import {
  EXPIRED_STORY_LINK_MESSAGE,
  STORY_LIFESPAN_HOURS,
  isExpiredStory,
} from '~publish/helpers/isExpiredStory'
import { getInstagramStoryUrl } from '~publish/helpers/getInstagramStoryUrl'
import { omitFields } from '~publish/helpers/object'
import { useDateTimeFormatter } from '~publish/hooks/useDateTimeFormatter'
import { usePostComposer } from '~publish/hooks/usePostComposer'
import { usePostPermalinksEnabled } from '~publish/hooks/usePostPermalinksEnabled'
import { useRelaxDraftValidationEnabled } from '~publish/hooks/useRelaxDraftValidationEnabled'
import { useTimezone } from '~publish/hooks/useTimezone'
import { isRpcUpdate } from '~publish/legacy/post/helpers'
import type { RpcUpdate } from '~publish/legacy/post/types'
import { communityPage } from '~publish/legacy/routes'
import { useFetchPostForComposer } from '~publish/hooks/postForComposer/useFetchPostForComposer'
import { useCommunityConfig } from '~publish/pages/Community/CommunityConfig'
import { canPerform } from '~publish/utils/permissions/canPerform'

import {
  useMetadataThread,
  usePostData,
  useTrackActionClicked,
} from '../PostCardContext'
import { useAddPostToQueue } from '../useAddPostToQueue'
import { useApprovePost } from '../useApprovePost'
import { useMovePostToDrafts } from '../useMovePostToDrafts'
import { usePublishPostNow } from '../usePublishPostNow'
import { useRejectPost } from '../useRejectPost'
import {
  useMovePostToBottomOfQueue,
  useMovePostToTopOfQueue,
} from '../useReorderPost'
import { useRequestPostApproval } from '../useRequestApproval'
import { useRevertPostApprovalRequest } from '../useRevertPostApprovalRequest'
import { AdminDeleteSentPostMenuItem } from './AdminDeleteSentPostMenuItem'
import { copyPostPermalinkToClipboard } from './copyPostPermalink'
import { DeletePostMenuItem } from './DeletePostMenuItem'
import {
  getMissingRequiredMediaMessage,
  getOverCharacterLimitMessage,
} from './helpers'
import { MoreActionsDropdown } from './MoreActionsDropdown'
import { SetRecurrenceMenuItem } from './SetRecurrenceMenuItem'
import { useDraftActionLabels } from './useDraftActionLabels'

import styles from './PostCardActions.module.css'

const omittedFields: (keyof RpcUpdate)[] = [
  'id',
  'due_at',
  'day',
  'scheduled_at',
  'scheduledAt',
  'serviceUpdateId',
  'createdAt',
  'statistics',
  'status',
  'serviceLink',
]

type OmittedField = (typeof omittedFields)[number]

type RpcUpdateWithoutOmittedFields = Omit<RpcUpdate, OmittedField>

const omitDuplicatedFields = (
  post: RpcUpdate,
): RpcUpdateWithoutOmittedFields => {
  const newPost = omitFields(post, omittedFields)

  return {
    ...newPost,
    // Reduce tags to IDs only for use in composer
    tags: post.tags?.map((tag) => tag.id),
    isSent: false,
    isPastDue: false,
    isDraft: false,
  }
}

/**
 * Tooltip copy for a queue/schedule/publish action the post cannot take yet
 * because its channel still requires media, or null when the action can run.
 */
const useMissingRequiredMediaMessage = (): string | null => {
  const { channel, assets, text, metadata } = usePostData()
  const relaxed = useRelaxDraftValidationEnabled()

  return getMissingRequiredMediaMessage({
    service: channel.service,
    postType: metadata?.type,
    assets,
    text,
    relaxed,
  })
}

const useOverCharacterLimitMessage = (): string | null => {
  const { channel, text, metadata } = usePostData()
  const thread = useMetadataThread(metadata)

  const service = Services.get(channel.service)

  // Mirrors useIsTwitterPremium / useMastodonMaxCharacters (src/hooks/
  // selectors/channel.ts): the limit depends on the channel's subscription
  // tier (X free is 280, paid tiers 25k) and, for Mastodon, the server
  // instance's own maximum.
  const isTwitterPremium =
    isOfType(channel.metadata, 'TwitterMetadata') &&
    (channel.metadata.subscriptionType === 'Basic' ||
      channel.metadata.subscriptionType === 'Premium' ||
      channel.metadata.subscriptionType === 'PremiumPlus')
  const mastodonMaxCharacters = isOfType(channel.metadata, 'MastodonMetadata')
    ? channel.metadata.maxCharacters
    : undefined

  const charLimit =
    typeof service?.charLimit === 'function'
      ? service.charLimit({ isTwitterPremium, mastodonMaxCharacters })
      : service?.charLimit

  return getOverCharacterLimitMessage({
    // channel.service also carries services char-toolkit has no counter for
    // (e.g. startPage); getCharsCountForService falls back to a plain count.
    service: channel.service as CharCountService,
    serviceName: service?.formattedName ?? channel.service,
    text,
    thread,
    charLimit,
  })
}

/**
 * The single reason a post cannot be published or queued yet, or null when it
 * can. Media requirement takes precedence over the character limit only because
 * one message shows at a time; both block the action identically.
 */
const usePublishBlockedMessage = (): string | null => {
  const mediaMessage = useMissingRequiredMediaMessage()
  const overCharacterLimitMessage = useOverCharacterLimitMessage()

  return mediaMessage ?? overCharacterLimitMessage
}

const ScheduleConfirmationPopover = ({
  children,
  ...props
}: PopoverProps): JSX.Element | null => {
  const { dueAt, id, postGroup } = usePostData()
  const [addPostToQueue, { loading }] = useAddPostToQueue()
  const trackActionClicked = useTrackActionClicked()
  const dateTimeFormatter = useDateTimeFormatter()
  const timeZone = useTimezone()
  const { triggerAttributes, editPostInComposer } = usePostComposer()

  const handleAddToQueue = useCallback(async () => {
    const { success, error } = await addPostToQueue({ variables: { id } })
    trackActionClicked('addToQueue-1')

    if (!success) {
      toast.error(`Failed to schedule post: ${error.message}`)
      return
    }

    toast.success(`Post scheduled`)
  }, [addPostToQueue, trackActionClicked, id])

  if (!dueAt) return null

  const formattedDate = dateTimeFormatter(dueAt, {
    dateFormat: "MMMM d 'at'",
    includeTimeZone: false,
  })

  return (
    <Popover {...props}>
      <Popover.Trigger>{children}</Popover.Trigger>
      <Popover.Content align="end">
        <Flex direction="column" gap="xs">
          <Text>Your post will be published on:</Text>
          <div className={styles.schedulePreview}>
            <Flex gap="xs" align="center">
              <SendClockIcon className={styles.icon} />
              <Text weight="medium">{formattedDate}</Text>
            </Flex>
            <Text size="sm" className={styles.timezone}>
              {formatTimeZone(timeZone)}
            </Text>
          </div>
        </Flex>
        <div className={styles.separator} />
        <Flex justify="end" gap="xs">
          <Button
            {...triggerAttributes}
            variant="secondary"
            onClick={(): Promise<void> =>
              editPostInComposer({
                postId: id,
                cta: 'publish-allChannels-postCard-editPost-1',
                contentItemId: postGroup?.id,
                contentItemTitle: postGroup?.title || null,
              })
            }
          >
            Change
          </Button>
          <Button
            variant="primary"
            loading={loading}
            onClick={handleAddToQueue}
          >
            Schedule Post
          </Button>
        </Flex>
      </Popover.Content>
    </Popover>
  )
}

const useIsExpiredStory = (): boolean => {
  const { metadata, sentAt } = usePostData()

  const postType = metadata?.type
  const [, refreshExpiry] = useState(0)
  const isExpired = isExpiredStory({ postType, sentAt })

  useEffect(() => {
    if (isExpired || postType !== 'story' || !sentAt) return

    const expiresAt = addHours(new Date(sentAt), STORY_LIFESPAN_HOURS).getTime()
    const delay = expiresAt - Date.now()
    if (!Number.isFinite(delay)) return

    const timeoutId = window.setTimeout(
      () => {
        refreshExpiry((revision) => revision + 1)
      },
      Math.max(0, delay),
    )

    return (): void => window.clearTimeout(timeoutId)
  }, [postType, sentAt, isExpired])

  return isExpired
}

const usePostExternalLink = (): string | null => {
  const { externalLink, channel, metadata, status } = usePostData()

  if (
    status === 'sent' &&
    channel.service === 'instagram' &&
    metadata?.type === 'story'
  ) {
    return getInstagramStoryUrl({ username: channel.name, externalLink })
  }

  return externalLink
}

/**
 * The network link as a flat menu item, for when post permalinks are off and
 * there is no Buffer link alongside it to be confused with.
 */
const CopyLinkMenuItem = (): JSX.Element | null => {
  const { allowedActions } = usePostData()
  const externalLink = usePostExternalLink()
  const trackActionClicked = useTrackActionClicked()
  const isExpired = useIsExpiredStory()

  const handleCopyLink = useCallback(async () => {
    if (!externalLink) {
      return
    }

    await navigator.clipboard.writeText(externalLink)
    trackActionClicked('copyLink-1')
    toast.success('Link copied to the clipboard')
  }, [externalLink, trackActionClicked])

  if (!externalLink || !allowedActions.includes('copyPostLink')) {
    return null
  }

  if (isExpired) {
    return (
      <Tooltip content={EXPIRED_STORY_LINK_MESSAGE} side="right" sideOffset={4}>
        <DropdownMenu.Item disabled>
          <LinkIcon />
          Copy link
        </DropdownMenu.Item>
      </Tooltip>
    )
  }

  return (
    <DropdownMenu.Item onSelect={handleCopyLink}>
      <LinkIcon />
      Copy link
    </DropdownMenu.Item>
  )
}

/** Companion to {@link CopyLinkMenuItem}, on the same flag. */
const ShareLinkMenuItem = (): JSX.Element | null => {
  const { allowedActions } = usePostData()
  const externalLink = usePostExternalLink()
  const trackActionClicked = useTrackActionClicked()
  const { triggerAttributes, createNewPostInComposer } = usePostComposer()
  const isExpired = useIsExpiredStory()

  if (!externalLink || !allowedActions.includes('sharePostLink')) {
    return null
  }

  if (isExpired) {
    return (
      <Tooltip content={EXPIRED_STORY_LINK_MESSAGE} side="right" sideOffset={4}>
        <DropdownMenu.Item disabled>
          <ForwardIcon />
          Share Link in a Post
        </DropdownMenu.Item>
      </Tooltip>
    )
  }

  return (
    <DropdownMenu.Item
      {...triggerAttributes}
      onSelect={(): Promise<void> => {
        trackActionClicked('shareLink-1')
        return createNewPostInComposer({
          cta: 'publish-allChannels-postCard-shareLink-1',
          prefillPostData: { text: externalLink },
        })
      }}
    >
      <ForwardIcon />
      Share Link in a Post
    </DropdownMenu.Item>
  )
}

/**
 * Available on every post whatever its status or the reader's access: the link is
 * not a capability, so whoever opens it still faces the same channel checks.
 */
const useCopyPostLink = (): (() => Promise<void>) => {
  const { id } = usePostData()
  const trackActionClicked = useTrackActionClicked()

  return useCallback(async () => {
    await copyPostPermalinkToClipboard(id)
    trackActionClicked('copyPostLink-1')
  }, [id, trackActionClicked])
}

const CopyPostLinkMenuItem = (): JSX.Element => {
  const handleCopyPostLink = useCopyPostLink()

  return (
    <DropdownMenu.Item onSelect={handleCopyPostLink}>
      <LinkIcon />
      Copy Post Link
    </DropdownMenu.Item>
  )
}

/** {@link CopyPostLinkMenuItem} for a footer where it would be the only menu item. */
const CopyPostLinkButton = (): JSX.Element => {
  const handleCopyPostLink = useCopyPostLink()

  return (
    <IconButton
      label="Copy Post Link"
      tooltip="Copy Post Link"
      onClick={handleCopyPostLink}
    >
      <LinkIcon />
    </IconButton>
  )
}

/**
 * The link on the social network, not the Buffer link {@link CopyPostLinkMenuItem}
 * copies. Nested under a submenu so the two can't be mistaken for each other.
 */
const LivePostLinkMenuItem = (): JSX.Element | null => {
  const { allowedActions, channel } = usePostData()
  const externalLink = usePostExternalLink()
  const trackActionClicked = useTrackActionClicked()
  const { triggerAttributes, createNewPostInComposer } = usePostComposer()
  const isExpired = useIsExpiredStory()

  const handleCopyLiveLink = useCallback(async () => {
    if (!externalLink) {
      return
    }

    try {
      await navigator.clipboard.writeText(externalLink)
    } catch {
      toast.error('Unable to copy link')
      return
    }
    trackActionClicked('copyLink-1')
    toast.success('Live post link copied')
  }, [externalLink, trackActionClicked])

  const canCopy = !!externalLink && allowedActions.includes('copyPostLink')
  // Sharing drafts a new post, so it needs channel publishing rights too — the
  // backend leaves `sharePostLink` in a view-only member's allowedActions.
  const canShare =
    !!externalLink &&
    allowedActions.includes('sharePostLink') &&
    canPerform(channel, 'manageUpdates')

  if (!canCopy && !canShare) {
    return null
  }

  // An expired Story's permalink 404s, leaving nothing actionable in the
  // submenu: collapse it to a single disabled row that explains why.
  if (isExpired) {
    return (
      <Tooltip content={EXPIRED_STORY_LINK_MESSAGE} side="right" sideOffset={4}>
        <DropdownMenu.Item disabled>
          <ExternalLinkIcon /> Live Post Link
        </DropdownMenu.Item>
      </Tooltip>
    )
  }

  // Copy is the only option left, so a submenu would hold one item. The label
  // keeps it distinct from the Buffer link.
  if (!canShare) {
    return (
      <DropdownMenu.Item onSelect={handleCopyLiveLink}>
        <ExternalLinkIcon /> Copy Live Post Link
      </DropdownMenu.Item>
    )
  }

  return (
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger>
        <ExternalLinkIcon /> Live Post Link
      </DropdownMenu.SubTrigger>
      <DropdownMenu.SubMenu>
        {canCopy && (
          <DropdownMenu.Item onSelect={handleCopyLiveLink}>
            <LinkIcon /> Copy
          </DropdownMenu.Item>
        )}
        {canShare && (
          <DropdownMenu.Item
            {...triggerAttributes}
            onSelect={(): Promise<void> => {
              trackActionClicked('shareLink-1')
              return createNewPostInComposer({
                cta: 'publish-allChannels-postCard-shareLink-1',
                prefillPostData: { text: externalLink ?? '' },
              })
            }}
          >
            <ForwardIcon /> Share in New Post
          </DropdownMenu.Item>
        )}
      </DropdownMenu.SubMenu>
    </DropdownMenu.Sub>
  )
}

const PublishNowMenuItem = (): JSX.Element | null => {
  const { id, channel, allowedActions, status, sharedNow } = usePostData()
  const trackActionClicked = useTrackActionClicked()
  const publishBlockedMessage = usePublishBlockedMessage()
  const shouldDisablePublishNow = status === 'sending' || sharedNow
  const [publishPostNow, { loading }] = usePublishPostNow()

  const handlePublishNow = useCallback(
    async (e: Event) => {
      e.preventDefault()
      const { success, data } = await publishPostNow({
        variables: { id },
      })
      trackActionClicked('publishNow-1')

      if (!success) {
        toast.error('Post publishing failed')
        return
      }

      const message =
        data?.post.status === 'sent'
          ? `Post published to ${channel.name}`
          : `Preparing your post to publish to ${channel.name}. It will go live shortly`

      toast.success(message)
    },
    [publishPostNow, id, channel.name, trackActionClicked],
  )

  if (!allowedActions.includes('publishPostNow')) {
    return null
  }

  if (publishBlockedMessage) {
    return (
      <Tooltip content={publishBlockedMessage} side="right">
        <DropdownMenu.Item disabled>
          <SendHorizontalIcon />
          Publish Now
        </DropdownMenu.Item>
      </Tooltip>
    )
  }

  return (
    <DropdownMenu.Item
      loading={loading}
      onSelect={handlePublishNow}
      disabled={shouldDisablePublishNow}
    >
      <SendHorizontalIcon />
      Publish Now
    </DropdownMenu.Item>
  )
}

const AddToQueueMenuItem = (): JSX.Element | null => {
  const { id, allowedActions, status } = usePostData()
  const trackActionClicked = useTrackActionClicked()
  const publishBlockedMessage = usePublishBlockedMessage()
  const [addPostToQueue, { loading }] = useAddPostToQueue()

  const handleAddToQueue = useCallback(
    async (e: Event) => {
      e.preventDefault()
      const { success, error } = await addPostToQueue({ variables: { id } })
      trackActionClicked('addToQueue-1')

      if (!success) {
        toast.error(`Failed to add post to queue: ${error.message}`)
        return
      }

      toast.success(`Post added to queue`)
    },
    [addPostToQueue, trackActionClicked, id],
  )

  if (
    !allowedActions.includes('addPostToQueue') ||
    status === 'needs_approval'
  ) {
    return null
  }

  if (publishBlockedMessage) {
    return (
      <Tooltip content={publishBlockedMessage} side="right">
        <DropdownMenu.Item disabled>
          <QueueBottomIcon />
          Add to Queue
        </DropdownMenu.Item>
      </Tooltip>
    )
  }

  return (
    <DropdownMenu.Item loading={loading} onSelect={handleAddToQueue}>
      <QueueBottomIcon />
      Add to Queue
    </DropdownMenu.Item>
  )
}

const EditPostButton = (): JSX.Element | null => {
  const { allowedActions, id, postGroup } = usePostData()
  const { triggerAttributes, editPostInComposer } = usePostComposer()
  const { editLabel } = useDraftActionLabels()

  if (!allowedActions.includes('updatePost')) {
    return null
  }

  return (
    <IconButton
      {...triggerAttributes}
      variant="secondary"
      label={editLabel}
      tooltip="Edit"
      onClick={(): Promise<void> =>
        editPostInComposer({
          postId: id,
          cta: 'publish-allChannels-postCard-editPost-1',
          contentItemId: postGroup?.id,
          contentItemTitle: postGroup?.title || null,
        })
      }
    >
      <PencilIcon />
    </IconButton>
  )
}

export const ViewPostMenuItem = ({
  hidden,
  onOpenGroupDialog,
}: {
  hidden?: boolean
  /**
   * When set, selecting the item opens the group-aware dialog in place
   * instead of navigating to the post details route.
   */
  onOpenGroupDialog?: () => void
}): JSX.Element | null => {
  const post = usePostData()
  const { search } = useLocation()

  if (hidden) {
    return null
  }

  if (onOpenGroupDialog) {
    return (
      // No aria-expanded: the menu (and this item) unmounts when the dialog
      // opens, so the attribute could never truthfully read true.
      <DropdownMenu.Item onSelect={onOpenGroupDialog} aria-haspopup="dialog">
        <ExpandIcon />
        Post Details
      </DropdownMenu.Item>
    )
  }

  const url = `posts/${post.id}${search}`

  return (
    <DropdownMenu.Item asChild>
      <Link to={url} aria-haspopup="dialog" aria-expanded="false">
        <ExpandIcon />
        Post Details
      </Link>
    </DropdownMenu.Item>
  )
}

const ViewPostDetailsButton = ({
  hidden,
  onOpenGroupDialog,
}: {
  hidden?: boolean
  /**
   * When set, clicking opens the group-aware dialog in place instead of
   * navigating to the post details route.
   */
  onOpenGroupDialog?: () => void
}): JSX.Element | null => {
  const { id } = usePostData()
  const { search } = useLocation()

  if (hidden) {
    return null
  }

  if (onOpenGroupDialog) {
    return (
      <Button
        variant="secondary"
        onClick={onOpenGroupDialog}
        aria-haspopup="dialog"
      >
        <ExpandIcon /> View Post Details
      </Button>
    )
  }

  return (
    <Button
      as={Link}
      variant="secondary"
      to={`posts/${id}${search}`}
      aria-haspopup="dialog"
      aria-expanded="false"
    >
      <ExpandIcon /> View Post Details
    </Button>
  )
}

export const DuplicateMenuItem = (): JSX.Element | null => {
  const [loading, setLoading] = useState(false)
  const { id, allowedActions, channel } = usePostData()
  const { triggerAttributes, duplicatePostInComposer } = usePostComposer()
  const fetchPostForComposer = useFetchPostForComposer()
  const trackActionClicked = useTrackActionClicked()
  const cta = 'publish-allChannels-postCard-duplicatePost-1'

  // Ingested posts only fetch thumbnails, so we do not have the media files available to duplicate
  const isDuplicateUnavailable = !allowedActions.includes('duplicatePost')

  const handleDuplicatePost = useCallback(async () => {
    setLoading(true)

    try {
      const post = await fetchPostForComposer(id)
      if (!isRpcUpdate(post)) {
        toast.error('Failed to load post for duplication')
        return
      }
      const prefillPostData = omitDuplicatedFields(post)

      void duplicatePostInComposer({
        cta,
        channels: [channel.id],
        prefillPostData,
        duplicatedFrom: id,
      })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err)
      toast.error(`Failed to duplicate post: ${message}`)
    } finally {
      setLoading(false)
      trackActionClicked('duplicate-1')
    }
  }, [
    id,
    duplicatePostInComposer,
    fetchPostForComposer,
    trackActionClicked,
    channel.id,
  ])

  // Duplicating drafts a new post, so a member who cannot post on the channel
  // never gets the action. The disabled row below explains only the other
  // reason `duplicatePost` is missing: an ingested post has no media to copy.
  if (!canPerform(channel, 'manageUpdates')) {
    return null
  }

  if (isDuplicateUnavailable) {
    return (
      <Tooltip
        content="We can only duplicate posts shared via Buffer"
        side="right"
        sideOffset={4}
      >
        <DropdownMenu.Item disabled>
          <CopyPlusIcon />
          Duplicate Post
        </DropdownMenu.Item>
      </Tooltip>
    )
  }

  return (
    <DropdownMenu.Item
      {...triggerAttributes}
      loading={loading}
      onClick={handleDuplicatePost}
    >
      <CopyPlusIcon />
      Duplicate Post
    </DropdownMenu.Item>
  )
}

const MoveToDraftsMenuItem = (): JSX.Element | null => {
  const { id, allowedActions } = usePostData()
  const trackActionClicked = useTrackActionClicked()
  const [movePostToDrafts, { loading }] = useMovePostToDrafts()

  const handleMoveToDrafts = useCallback(
    async (e: Event) => {
      e.preventDefault()
      const { data, error } = await movePostToDrafts({ variables: { id } })
      trackActionClicked('moveToDrafts-1')

      if (data) {
        toast.success('Post moved to drafts')
      }

      if (error) {
        toast.error(`Failed to move post to drafts: ${error.message}`)
      }
    },
    [movePostToDrafts, trackActionClicked, id],
  )

  if (!allowedActions.includes('movePostToDraft')) {
    return null
  }

  return (
    <DropdownMenu.Item loading={loading} onSelect={handleMoveToDrafts}>
      <FileOutputIcon />
      Move to Drafts
    </DropdownMenu.Item>
  )
}

const AddToQueueButton = (): JSX.Element | null => {
  const { id, allowedActions } = usePostData()
  const trackActionClicked = useTrackActionClicked()
  const publishBlockedMessage = usePublishBlockedMessage()
  const [addPostToQueue, { loading }] = useAddPostToQueue()

  const handleAddToQueue = useCallback(async () => {
    const { success, error } = await addPostToQueue({ variables: { id } })
    trackActionClicked('addToQueue-1')

    if (!success) {
      toast.error(`Failed to add post to queue: ${error.message}`)
      return
    }

    toast.success(`Post added to queue`)
  }, [addPostToQueue, trackActionClicked, id])

  if (!allowedActions.includes('addPostToQueue')) {
    return null
  }

  if (publishBlockedMessage) {
    return (
      <Tooltip content={publishBlockedMessage} side="top">
        {/* Wrapper is the tooltip trigger: a disabled <button> doesn't fire
            hover events, so the tooltip can't anchor to it directly. */}
        <span className={styles.disabledActionWrapper}>
          <Button
            data-testid="add-to-queue-button"
            variant="secondary"
            disabled
          >
            <QueueBottomIcon /> Add to Queue
          </Button>
        </span>
      </Tooltip>
    )
  }

  return (
    <Button
      data-testid="add-to-queue-button"
      variant="secondary"
      loading={loading}
      onClick={handleAddToQueue}
    >
      <QueueBottomIcon /> Add to Queue
    </Button>
  )
}

const ReorderMenuItem = (): JSX.Element | null => {
  const { allowedActions, id } = usePostData()
  const trackActionClicked = useTrackActionClicked()
  const { pathname } = useLocation()
  const [movePostToTopOfQueue, { loading: loadingMoveToTop }] =
    useMovePostToTopOfQueue()
  const [movePostToBottomOfQueue, { loading: loadingMoveToBottom }] =
    useMovePostToBottomOfQueue()

  const isAllChannelsOrScheduleView =
    pathname.startsWith('/all-channels') || pathname.startsWith('/schedule')

  const handleMoveToTop = useCallback(async (): Promise<void> => {
    const { success, error } = await movePostToTopOfQueue({
      variables: {
        id,
      },
    })
    trackActionClicked('moveToTop-1')
    if (!success) {
      toast.error(`Failed to move post to top of queue: ${error.message}`)
      return
    }

    toast.success(`Post moved to top of queue`)
  }, [trackActionClicked, id, movePostToTopOfQueue])

  const handleMoveToBottom = useCallback(async (): Promise<void> => {
    const { success, error } = await movePostToBottomOfQueue({
      variables: {
        id,
      },
    })
    trackActionClicked('moveToBottom-1')
    if (!success) {
      toast.error(`Failed to move post to bottom of queue: ${error.message}`)
      return
    }

    toast.success(`Post moved to bottom of queue`)
  }, [trackActionClicked, id, movePostToBottomOfQueue])

  // Hide reorder button if
  // - the user does not have permission to update the post
  // - or is on the all channels view
  if (!allowedActions.includes('updatePost') || isAllChannelsOrScheduleView) {
    return null
  }

  return (
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger>
        <ArrowDownUpIcon /> Reorder
      </DropdownMenu.SubTrigger>
      <DropdownMenu.SubMenu className={styles.reorderMenu}>
        <DropdownMenu.Item
          loading={loadingMoveToTop}
          onSelect={handleMoveToTop}
        >
          <ArrowUpIcon /> Move to Top
        </DropdownMenu.Item>
        <DropdownMenu.Item
          loading={loadingMoveToBottom}
          onSelect={handleMoveToBottom}
        >
          <ArrowDownIcon /> Move to Next Available Slot
        </DropdownMenu.Item>
      </DropdownMenu.SubMenu>
    </DropdownMenu.Sub>
  )
}

const PublishNowButton = (): JSX.Element | null => {
  const { id, channel, allowedActions, status, sharedNow, dueAt } =
    usePostData()
  const [publishPostNow, { loading }] = usePublishPostNow()
  const trackActionClicked = useTrackActionClicked()
  const publishBlockedMessage = usePublishBlockedMessage()

  // Disable publish now if the post is past due and within 60 minutes
  // or if the post is in a sending state
  // TODO: Refactor this to use a realtime update that checks the posts status
  // currently we do not have a realtime update for the post status
  const isPastDue = dueAt && isPast(dueAt)
  const isOverdueWithinHour =
    dueAt && isPastDue && isAfter(addMinutes(new Date(dueAt), 60), new Date())
  const shouldDisablePublishNow =
    status === 'sending' ||
    sharedNow ||
    Boolean(isPastDue && isOverdueWithinHour)

  const handlePublishNow = useCallback(async () => {
    const { success, data } = await publishPostNow({ variables: { id } })
    trackActionClicked('publishNow-1')

    if (!success) {
      toast.error('Post publishing failed')
      return
    }

    const message =
      data?.post.status === 'sent'
        ? `Post published to ${channel.name}`
        : `Preparing your post to publish to ${channel.name}. It will go live shortly`

    toast.success(message)
  }, [id, publishPostNow, trackActionClicked, channel.name])

  if (!allowedActions.includes('publishPostNow')) {
    return null
  }

  if (publishBlockedMessage) {
    return (
      <Tooltip content={publishBlockedMessage} side="top">
        {/* Wrapper is the tooltip trigger: a disabled <button> doesn't fire
            hover events, so the tooltip can't anchor to it directly. */}
        <span className={styles.disabledActionWrapper}>
          <Button variant="secondary" disabled>
            <SendHorizontalIcon /> Publish Now
          </Button>
        </span>
      </Tooltip>
    )
  }

  if (shouldDisablePublishNow) {
    return (
      <Tooltip
        content="Some media posts may need a moment to process and publish."
        side="top"
        sideOffset={4}
      >
        <Button variant="secondary" disabled>
          <SendHorizontalIcon /> Publish Now
        </Button>
      </Tooltip>
    )
  }

  return (
    <Button
      variant="secondary"
      loading={loading}
      onClick={handlePublishNow}
      disabled={shouldDisablePublishNow}
    >
      <SendHorizontalIcon /> Publish Now
    </Button>
  )
}

const RetryNowButton = (): JSX.Element | null => {
  const { id, channel, allowedActions, status, sharedNow } = usePostData()
  const [publishPostNow, { loading }] = usePublishPostNow()
  const shouldDisablePublishNow = status === 'sending' || sharedNow
  const trackActionClicked = useTrackActionClicked()

  const handleRetryNow = useCallback(async () => {
    const { success, data } = await publishPostNow({ variables: { id } })
    trackActionClicked('retryNow-1')

    if (!success) {
      toast.error('Post publishing failed')
      return
    }

    const message =
      data?.post.status === 'sent'
        ? `Post published to ${channel.name}`
        : `Preparing your post to publish to ${channel.name}. It will go live shortly`

    toast.success(message)
  }, [id, publishPostNow, trackActionClicked, channel.name])

  if (!allowedActions.includes('publishPostNow')) {
    return null
  }

  return (
    <Button
      variant="secondary"
      loading={loading}
      onClick={handleRetryNow}
      disabled={shouldDisablePublishNow}
    >
      <SendHorizontalIcon /> Retry Now
    </Button>
  )
}

const SchedulePostButton = (): JSX.Element | null => {
  const { allowedActions } = usePostData()
  const trackActionClicked = useTrackActionClicked()
  const publishBlockedMessage = usePublishBlockedMessage()

  if (!allowedActions.includes('addPostToQueue')) {
    return null
  }

  if (publishBlockedMessage) {
    return (
      <Tooltip content={publishBlockedMessage} side="top">
        {/* Wrapper is the tooltip trigger: a disabled <button> doesn't fire
            hover events, so the tooltip can't anchor to it directly. */}
        <span className={styles.disabledActionWrapper}>
          <Button variant="secondary" disabled>
            <SendClockIcon /> Schedule Post
          </Button>
        </span>
      </Tooltip>
    )
  }

  return (
    <ScheduleConfirmationPopover>
      <Button
        variant="secondary"
        onClick={(): void => trackActionClicked('schedulePost-1')}
      >
        <SendClockIcon /> Schedule Post
      </Button>
    </ScheduleConfirmationPopover>
  )
}

const ReschedulePostButton = (): JSX.Element | null => {
  const { allowedActions, id, postGroup } = usePostData()
  const { triggerAttributes, editPostInComposer } = usePostComposer()
  const trackActionClicked = useTrackActionClicked()

  if (!allowedActions.includes('updatePost')) {
    return null
  }

  return (
    <Button
      {...triggerAttributes}
      variant="secondary"
      onClick={(): Promise<void> => {
        trackActionClicked('reschedule-1')
        return editPostInComposer({
          postId: id,
          cta: 'publish-allChannels-postCard-editPost-1',
          contentItemId: postGroup?.id,
          contentItemTitle: postGroup?.title || null,
        })
      }}
    >
      <SendClockIcon /> Reschedule
    </Button>
  )
}

const RequestApprovalButton = (): JSX.Element | null => {
  const { id, allowedActions } = usePostData()
  const [requestApproval, { loading }] = useRequestPostApproval()
  const trackActionClicked = useTrackActionClicked()

  const handleRequestApproval = useCallback(async () => {
    const { error, success } = await requestApproval({
      variables: { id },
    })
    trackActionClicked('requestApproval-1')

    if (!success) {
      toast.error(`Failed to request approval: ${error.message}`)
      return
    }

    toast.success('Approval requested')
  }, [requestApproval, trackActionClicked, id])

  if (!allowedActions.includes('requestPostApproval')) {
    return null
  }

  return (
    <Button variant="primary" loading={loading} onClick={handleRequestApproval}>
      <UserRoundCheckIcon /> Request Approval
    </Button>
  )
}

const RevertApprovalRequestButton = (): JSX.Element | null => {
  const { id, allowedActions } = usePostData()
  const [revertApprovalRequest, { loading }] = useRevertPostApprovalRequest()
  const trackActionClicked = useTrackActionClicked()

  const handleRevertApprovalRequest = useCallback(async () => {
    const { error, success } = await revertApprovalRequest({
      variables: { id },
    })
    trackActionClicked('revertApprovalRequest-1')

    if (!success) {
      toast.error(`Failed to revert approval request. ${error.message}`)
      return
    }

    toast.success('Post moved to back to drafts')
  }, [revertApprovalRequest, trackActionClicked, id])

  if (!allowedActions.includes('revertPostApprovalRequest')) {
    return null
  }

  return (
    <Button
      variant="secondary"
      loading={loading}
      onClick={handleRevertApprovalRequest}
    >
      <XIcon /> Revert Approval Request
    </Button>
  )
}

/**
 * Approves the post in context. Shared by the footer button and the Board
 * card menu item so both track and report the same way.
 */
export const useApprovePostAction = (): {
  approve: () => Promise<void>
  label: string
  loading: boolean
} => {
  const { id, isCustomScheduled } = usePostData()
  const [approvePost, { loading }] = useApprovePost()
  const cta = isCustomScheduled ? 'Schedule' : 'AddToQueue'
  const trackActionClicked = useTrackActionClicked()

  const approve = useCallback(async () => {
    const { error, success } = await approvePost({
      variables: { id },
    })
    trackActionClicked(`approveAnd${cta}-1`)

    if (!success) {
      toast.error(`Failed to approve post. ${error.message}`)
      return
    }

    toast.success('Post approved')
  }, [approvePost, trackActionClicked, cta, id])

  const label = isCustomScheduled ? 'Schedule Post' : 'Add to Queue'

  return { approve, label, loading }
}

/**
 * Rejects the post in context. Shared by the footer button and the Board card
 * menu item so both track and report the same way.
 */
export const useRejectPostAction = (): {
  reject: () => Promise<void>
  loading: boolean
} => {
  const { id } = usePostData()
  const [rejectPost, { loading }] = useRejectPost()
  const trackActionClicked = useTrackActionClicked()

  const reject = useCallback(async () => {
    const { error, success } = await rejectPost({
      variables: { id },
    })
    trackActionClicked('reject-1')

    if (!success) {
      toast.error(`Failed to reject post. ${error.message}`)
      return
    }

    toast.success('Post rejected and moved back to drafts')
  }, [rejectPost, trackActionClicked, id])

  return { reject, loading }
}

const ApprovePostButton = (): JSX.Element | null => {
  const { allowedActions } = usePostData()
  const { approve, label, loading } = useApprovePostAction()

  if (!allowedActions.includes('approvePost')) {
    return null
  }

  return (
    <Button variant="primary" loading={loading} onClick={approve}>
      <CheckIcon /> {label}
    </Button>
  )
}

const RejectPostButton = (): JSX.Element | null => {
  const { allowedActions } = usePostData()
  const { reject, loading } = useRejectPostAction()

  if (!allowedActions.includes('rejectPost')) {
    return null
  }

  return (
    <Button variant="critical" loading={loading} onClick={reject}>
      <XIcon /> Reject
    </Button>
  )
}

const ViewPostButton = ({
  permalinksEnabled,
}: {
  permalinksEnabled: boolean
}): JSX.Element | null => {
  const { allowedActions, channel } = usePostData()
  const externalLink = usePostExternalLink()
  const trackActionClicked = useTrackActionClicked()
  const isExpired = useIsExpiredStory()
  // Only worth spelling out "Live" while the Live Post Link submenu is there to
  // be told apart from the Buffer link.
  const label = permalinksEnabled ? 'Go to Live Post' : 'Go to post'

  if (
    !externalLink ||
    !allowedActions.includes('viewPost') ||
    (channel.service === 'instagram' && isExpired)
  ) {
    return null
  }

  if (isExpired) {
    return (
      <Tooltip content={EXPIRED_STORY_LINK_MESSAGE}>
        {/* Wrapper is the tooltip trigger: a disabled <button> doesn't fire
            hover events, so the tooltip can't anchor to it directly. */}
        <span className={styles.disabledActionWrapper}>
          <Button variant="secondary" disabled>
            <ExternalLinkIcon /> {label}
          </Button>
        </span>
      </Tooltip>
    )
  }

  return (
    <Tooltip content={`Open post on ${normalizeNetworkName(channel.service)}`}>
      <Button
        as="a"
        href={externalLink}
        target="_blank"
        rel="noopener noreferrer"
        variant="secondary"
        onClick={(): void => trackActionClicked('viewPost-1')}
      >
        <ExternalLinkIcon /> {label}
      </Button>
    </Tooltip>
  )
}

const ViewCommentsMenuItem = (): JSX.Element | null => {
  const { status, channel, metrics, id: postId } = usePostData()
  const trackActionClicked = useTrackActionClicked()
  const { config: commentsConfig } = useCommunityConfig()

  const hasComments = metrics?.find(
    ({ type, value }) => type === 'comments' && value > 0,
  )

  const isChannelSupported = commentsConfig.isSupportedInComments(channel)

  if (status !== 'sent' || !hasComments || !isChannelSupported) {
    return null
  }

  const communityUrl = communityPage.getViewRoute({ view: 'posts', postId })

  return (
    <DropdownMenu.Item asChild>
      <Link
        to={communityUrl}
        onClick={(): void => trackActionClicked('viewComments-1')}
      >
        <MessageSquareQuoteIcon />
        View Comments
      </Link>
    </DropdownMenu.Item>
  )
}

const DeveloperDataMenuItems = (): JSX.Element | null => {
  const { id } = usePostData()
  const shouldShow = useShouldShowDeveloperData()

  if (!shouldShow) {
    return null
  }

  return (
    <MoreActionsDropdown.Group>
      <DropdownMenu.Label>Developer Data</DropdownMenu.Label>
      <CopyIdMenuItem
        label="Copy Update ID"
        toastMessage="Update ID copied to clipboard!"
        id={id}
      />
      <OpenInHQMenuItem label="Open in HQ" type="post" id={id} />
      <AdminDeleteSentPostMenuItem />
    </MoreActionsDropdown.Group>
  )
}

type PostCardActionsProps = {
  className?: string
  hideViewPostDetails?: boolean
  /** Called after a successful delete, e.g. to navigate away from a route
   * that identifies the post by id. */
  onDeleted?: () => void
}

export const PostCardActions = ({
  className,
  hideViewPostDetails = false,
  onDeleted,
}: PostCardActionsProps): JSX.Element | null => {
  const { id, status, dueAt, isCustomScheduled, postGroup, channel } =
    usePostData()
  const { moreActionsLabel } = useDraftActionLabels()
  const showDeveloperData = useShouldShowDeveloperData()

  // With the group UI on, "Post Details" opens the group-aware dialog in
  // place; otherwise it navigates to the post details route.
  const { isReady, isEnabled } = useSplitEnabled(POST_GROUPS_UI_FLAG)
  const permalinksEnabled = usePostPermalinksEnabled()
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false)
  // The menu item that opens the dialog unmounts with its menu, so the dialog
  // restores focus to the menu's persistent trigger button on close.
  const moreActionsTriggerRef = useRef<HTMLButtonElement>(null)
  const trackDetailsActionClicked = useTrackActionClicked()
  const openGroupDialog =
    isReady && isEnabled
      ? (): void => {
          // The action measures group-details opens (CORE-5527), so only fire
          // it for posts that belong to a group — ungrouped posts get the
          // plain single-post view in the same dialog.
          if (postGroup?.id) {
            trackDetailsActionClicked('postGroupDetailsMoreActions-1')
          }
          setDetailsDialogOpen(true)
        }
      : undefined
  // Cheap to render per card: the dialog's queries live inside its content,
  // which Radix only renders while the dialog is open.
  const detailsDialog = openGroupDialog ? (
    <PostGroupDialog
      postId={id}
      postGroupId={postGroup?.id}
      open={detailsDialogOpen}
      onOpenChange={setDetailsDialogOpen}
      returnFocusTo={moreActionsTriggerRef}
    />
  ) : null
  const actionsClassName = clsx(styles.wrapper, className)
  // Outside sent posts the live link and comments don't apply, so without
  // developer data Copy Post Link is the only item and gets a button instead.
  const hasOnlyCopyPostLink = status !== 'sent' && !showDeveloperData

  // A post mid-send takes no publishing action, but its link still resolves, so
  // it gets the menu rather than the actionless fallback. Ahead of the read-only
  // branch because copying needs no channel rights.
  if (permalinksEnabled && status === 'sending') {
    return (
      <div className={actionsClassName}>
        {hasOnlyCopyPostLink ? (
          <CopyPostLinkButton />
        ) : (
          <MoreActionsDropdown key="sending" triggerRef={moreActionsTriggerRef}>
            <MoreActionsDropdown.Group>
              <CopyPostLinkMenuItem />
            </MoreActionsDropdown.Group>
            <DeveloperDataMenuItems />
          </MoreActionsDropdown>
        )}
        {detailsDialog}
      </div>
    )
  }

  // Read-only footer for members who can't manage posts on this channel.
  if (!canPerform(channel, 'manageUpdates') && permalinksEnabled) {
    // Every status, not just sent: sharing isn't a publishing action.
    return (
      <div className={actionsClassName}>
        <ViewPostDetailsButton
          hidden={hideViewPostDetails}
          onOpenGroupDialog={openGroupDialog}
        />
        {hasOnlyCopyPostLink ? (
          <CopyPostLinkButton />
        ) : (
          <MoreActionsDropdown
            key="read-only"
            triggerRef={moreActionsTriggerRef}
          >
            <MoreActionsDropdown.Group>
              <CopyPostLinkMenuItem />
              <LivePostLinkMenuItem />
            </MoreActionsDropdown.Group>
            <MoreActionsDropdown.Group>
              <ViewCommentsMenuItem />
            </MoreActionsDropdown.Group>
            <DeveloperDataMenuItems />
          </MoreActionsDropdown>
        )}
        {detailsDialog}
      </div>
    )
  }

  // Without the feature, a read-only member only ever had actions on a sent
  // post, and a post mid-send fell through to the actionless fallback below.
  if (!canPerform(channel, 'manageUpdates') && status !== 'sending') {
    const sentMenu =
      status === 'sent' ? (
        <MoreActionsDropdown
          key="sent-read-only"
          triggerRef={moreActionsTriggerRef}
        >
          <MoreActionsDropdown.Group>
            <CopyLinkMenuItem />
          </MoreActionsDropdown.Group>
          <MoreActionsDropdown.Group>
            <ViewCommentsMenuItem />
          </MoreActionsDropdown.Group>
          <DeveloperDataMenuItems />
        </MoreActionsDropdown>
      ) : null

    // Inside the post details dialog the details button is redundant, so a
    // non-sent post has no visible actions — keep the group dialog mounted
    // without an empty footer wrapper.
    if (hideViewPostDetails && sentMenu === null) {
      return detailsDialog
    }

    return (
      <div className={actionsClassName}>
        <ViewPostDetailsButton
          hidden={hideViewPostDetails}
          onOpenGroupDialog={openGroupDialog}
        />
        {sentMenu}
        {detailsDialog}
      </div>
    )
  }

  if (status === 'draft') {
    const isOverDue = dueAt && isPast(dueAt)
    const shouldAddToQueue = !isOverDue && !isCustomScheduled
    return (
      <div className={actionsClassName}>
        <RequestApprovalButton />
        {isOverDue && <ReschedulePostButton />}
        {isCustomScheduled && !isOverDue && <SchedulePostButton />}
        {shouldAddToQueue && <AddToQueueButton />}
        <EditPostButton />
        <MoreActionsDropdown
          key="draft"
          label={moreActionsLabel}
          triggerRef={moreActionsTriggerRef}
        >
          <MoreActionsDropdown.Group>
            <PublishNowMenuItem />
            <DuplicateMenuItem />
            <ViewPostMenuItem
              hidden={hideViewPostDetails}
              onOpenGroupDialog={openGroupDialog}
            />
          </MoreActionsDropdown.Group>
          <MoreActionsDropdown.Group>
            {permalinksEnabled && <CopyPostLinkMenuItem />}
          </MoreActionsDropdown.Group>
          <MoreActionsDropdown.Group>
            <DeletePostMenuItem onDeleted={onDeleted} />
          </MoreActionsDropdown.Group>
          <DeveloperDataMenuItems />
        </MoreActionsDropdown>
        {detailsDialog}
      </div>
    )
  }

  if (status === 'needs_approval') {
    return (
      <div className={clsx(actionsClassName, styles.approvalActions)}>
        <ApprovePostButton />
        <RejectPostButton />
        <RevertApprovalRequestButton />
        <div className={styles.iconActions}>
          <EditPostButton />
          <MoreActionsDropdown
            key="needs_approval"
            triggerRef={moreActionsTriggerRef}
          >
            <MoreActionsDropdown.Group>
              <PublishNowMenuItem />
              <AddToQueueMenuItem />
              <DuplicateMenuItem />
              <ViewPostMenuItem
                hidden={hideViewPostDetails}
                onOpenGroupDialog={openGroupDialog}
              />
            </MoreActionsDropdown.Group>
            <MoreActionsDropdown.Group>
              {permalinksEnabled && <CopyPostLinkMenuItem />}
            </MoreActionsDropdown.Group>
            <MoreActionsDropdown.Group>
              <DeletePostMenuItem onDeleted={onDeleted} />
            </MoreActionsDropdown.Group>
            <DeveloperDataMenuItems />
          </MoreActionsDropdown>
        </div>
        {detailsDialog}
      </div>
    )
  }

  if (status === 'scheduled') {
    return (
      <div className={actionsClassName}>
        <PublishNowButton />
        <EditPostButton />
        <MoreActionsDropdown key="scheduled" triggerRef={moreActionsTriggerRef}>
          <MoreActionsDropdown.Group>
            <MoveToDraftsMenuItem />
            <DuplicateMenuItem />
            <SplitProtected name="publish-recurring-posts">
              <SetRecurrenceMenuItem />
            </SplitProtected>
            <ReorderMenuItem />
            <ViewPostMenuItem
              hidden={hideViewPostDetails}
              onOpenGroupDialog={openGroupDialog}
            />
          </MoreActionsDropdown.Group>
          <MoreActionsDropdown.Group>
            {permalinksEnabled && <CopyPostLinkMenuItem />}
          </MoreActionsDropdown.Group>
          <MoreActionsDropdown.Group>
            <DeletePostMenuItem onDeleted={onDeleted} />
          </MoreActionsDropdown.Group>
          <DeveloperDataMenuItems />
        </MoreActionsDropdown>
        {detailsDialog}
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className={actionsClassName}>
        <RetryNowButton />
        <EditPostButton />
        <MoreActionsDropdown key="error" triggerRef={moreActionsTriggerRef}>
          <MoreActionsDropdown.Group>
            <AddToQueueMenuItem />
            <DuplicateMenuItem />
            <SplitProtected name="publish-recurring-posts">
              <SetRecurrenceMenuItem />
            </SplitProtected>
            <ViewPostMenuItem
              hidden={hideViewPostDetails}
              onOpenGroupDialog={openGroupDialog}
            />
          </MoreActionsDropdown.Group>
          <MoreActionsDropdown.Group>
            {permalinksEnabled && <CopyPostLinkMenuItem />}
          </MoreActionsDropdown.Group>
          <MoreActionsDropdown.Group>
            <DeletePostMenuItem onDeleted={onDeleted} />
          </MoreActionsDropdown.Group>
          <DeveloperDataMenuItems />
        </MoreActionsDropdown>
        {detailsDialog}
      </div>
    )
  }

  if (status === 'sent') {
    return (
      <div className={actionsClassName}>
        <ViewPostButton permalinksEnabled={permalinksEnabled} />

        <MoreActionsDropdown key="sent" triggerRef={moreActionsTriggerRef}>
          <MoreActionsDropdown.Group>
            <ViewPostMenuItem
              hidden={hideViewPostDetails}
              onOpenGroupDialog={openGroupDialog}
            />
            <DuplicateMenuItem />
          </MoreActionsDropdown.Group>
          <MoreActionsDropdown.Group>
            {permalinksEnabled && <CopyPostLinkMenuItem />}
            {permalinksEnabled && <LivePostLinkMenuItem />}
            {!permalinksEnabled && <ShareLinkMenuItem />}
            {!permalinksEnabled && <CopyLinkMenuItem />}
          </MoreActionsDropdown.Group>
          <MoreActionsDropdown.Group>
            <ViewCommentsMenuItem />
          </MoreActionsDropdown.Group>
          <DeveloperDataMenuItems />
        </MoreActionsDropdown>
        {detailsDialog}
      </div>
    )
  }

  // Defensive default for an unhandled status. It must still render the dialog:
  // Publish Now from inside the dialog flips the status, and returning null
  // here would unmount the open dialog mid-action (then pop it back open
  // when the status settles, since the open-state survives in this fiber).
  return detailsDialog
}
